"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 xl:gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-serif text-lg font-medium tracking-wide text-foreground transition-all hover:text-primary hover:underline hover:decoration-ring hover:underline-offset-4 hover:decoration-[1px]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-serif text-2xl font-medium text-foreground py-3 border-b border-border/40 last:border-0 hover:text-ring transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Intestazione() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="intestazione" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 ease-in-out", isScrolled ? "bg-background/95 backdrop-blur-md border-border shadow-sm" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 md:h-24 flex items-center justify-between relative">
        
        <div className="flex-1 flex items-center justify-start">
          <div className="lg:hidden">
            <SafeHydrate><Drawer>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-transparent -ml-2">
                  <Menu className="size-7 text-foreground" />
                  <span className="sr-only">Menu</span>
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <DrawerTitle className="font-serif text-2xl text-primary">
                      Menu
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon">
                        <X className="size-5" />
                      </Button>
                    </DrawerClose>
                  </div>
                </DrawerHeader>
                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="24">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
                <DrawerFooter className="border-t pt-4">
                  <Button className="w-full font-serif text-lg bg-primary text-primary-foreground" size="lg" asChild>
                    <Link to="https://wa.me/+393402498369">Ordina su WhatsApp</Link>
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>

          <div className="hidden lg:block">
            <MenuProvider menu_id="24">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex justify-center">
          <Link to="/" className="block transition-transform hover:scale-105 duration-300">
            <img src="https://dolcisaporisciacca.blueowlmedia.it/wp-content/uploads/2026/02/image_x5pul30q.png" alt="Dolci Sapori Logo" className="h-10 md:h-14 w-auto object-contain" />
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-end gap-2 md:gap-4">
          <Button asChild className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-serif tracking-wide px-6 shadow-sm">
            <Link to="https://wa.me/+393402498369" newTab>
              Ordina Ora
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}